import type { Mark, Gender, RawMark } from '@/types/domain';
import { getBasePrueba } from '@/config/sectores';
import { stripDiacritics } from '@/lib/normalize';

const HIGHER_BETTER = [
  'longitud',
  'triple',
  'altura',
  'pértiga',
  'peso',
  'disco',
  'jabalina',
  'martillo',
  'bala',
  'pentatlón',
  'heptatlón',
  'decatlón',
  'triatlón',
  'biatlón',
  'tetralón',
  // Nombres en INGLÉS (fuente World Athletics, 2026-08-06: el resumen del
  // disco salía ordenado de menor a mayor — "Discus Throw" no casaba).
  'throw',
  'shot put',
  'jump',
  'vault',
  'heptathlon',
  'decathlon',
];

export function isHigherBetter(prueba: string): boolean {
  const p = prueba.toLowerCase();
  return HIGHER_BETTER.some((x) => p.includes(x));
}

/**
 * G2 — Normaliza el nombre de la prueba:
 *  - "1/4 milla" → "400m"
 *  - "1/2 milla" → "800m"
 *  - Elimina sufijo "-Combis" (se conserva el nombre de la prueba parcial)
 *  - Los crosses (Cross / Trail) se agrupan a "Cross" / "Trail" (cada cross
 *    es distinto, por eso no son comparables — ver isComparablePrueba abajo).
 */
export function normalizePruebaName(p: string): string {
  if (!p) return p;
  let out = p.trim();

  // Millas → metros
  out = out.replace(/\b1\s*\/\s*4\s*milla\b/i, '400m');
  out = out.replace(/\b1\s*\/\s*2\s*milla\b/i, '800m');
  out = out.replace(/\bcuarto de milla\b/i, '400m');
  out = out.replace(/\bmedia milla\b/i, '800m');

  // Quitar sufijo -Combis (con o sin espacios, mayúsculas, guion largo, etc.)
  out = out.replace(/\s*[-—–]\s*combis\s*$/i, '');
  out = out.replace(/\s*\(combis\)\s*$/i, '');

  return out.trim();
}

/**
 * G2 — Devuelve `false` para pruebas cuyas marcas NO son comparables entre sí.
 * Concretamente Cross y Trail: cada carrera tiene distancia y desnivel propios.
 */
export function isComparablePrueba(prueba: string): boolean {
  const p = (prueba || '').toLowerCase();
  if (p.startsWith('cross')) return false;
  if (p.startsWith('trail')) return false;
  return true;
}

export function parseMarkToNum(_prueba: string, marca: string | null | undefined): number | null {
  if (!marca || marca === '-' || marca === 'NM' || marca === 'DNS') return null;
  if (marca.includes(':')) {
    const parts = marca.split(':');
    if (parts.length === 2) return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
    if (parts.length === 3) {
      return parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2]);
    }
  }
  const n = parseFloat(marca.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function isBetterMark(prueba: string, a: number | null, b: number | undefined): boolean {
  if (a === null) return false;
  if (b === undefined) return true;
  return isHigherBetter(prueba) ? a > b : a < b;
}

export interface PbSbMaps {
  pb: Record<string, number>;
  sb: Record<string, number>;
}

export function computePbSb(data: Mark[]): PbSbMaps {
  const pb: Record<string, number> = {};
  const sb: Record<string, number> = {};
  for (const r of data) {
    // G2 — No calcular PB/SB para Cross/Trail: cada carrera es única.
    if (!isComparablePrueba(r.prueba)) continue;
    const val = parseMarkToNum(r.prueba, r.marca);
    if (val === null) continue;
    const pbKey = `${r.atleta}|${r.prueba}`;
    const sbKey = `${r.atleta}|${r.prueba}|${r.temporada}`;
    if (isBetterMark(r.prueba, val, pb[pbKey])) pb[pbKey] = val;
    if (isBetterMark(r.prueba, val, sb[sbKey])) sb[sbKey] = val;
  }
  return { pb, sb };
}

/**
 * Los rankings RFEA listan la misma marca de un atleta en cada categoría en la
 * que es elegible (p.ej. una Sub20 que también puntúa en Absoluta y Sub23).
 * Tras importar el upload aparecen 2-3 filas idénticas excepto `categoria`.
 * Aquí colapsamos los duplicados conservando la categoría "Absoluta" cuando
 * está disponible (es la más informativa para análisis de rendimiento).
 *
 * Clave de duplicado: atleta + prueba + fecha + marca + pista.
 */
export function dedupeRawMarks(rows: RawMark[]): RawMark[] {
  const byKey = new Map<string, RawMark>();
  for (const r of rows) {
    const key = `${r.atleta}|${r.prueba}|${r.fecha}|${r.marca}|${r.pista}`;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, r);
      continue;
    }
    // Si el existente NO es Absoluta y el nuevo SÍ, reemplaza.
    const prevAbs = (prev.categoria || '').toLowerCase().startsWith('absoluta');
    const curAbs = (r.categoria || '').toLowerCase().startsWith('absoluta');
    if (!prevAbs && curAbs) byKey.set(key, r);
  }
  return [...byKey.values()];
}

export function processMarks(raw: RawMark[]): Mark[] {
  return raw
    .filter((r) => !(r.atleta && r.atleta.includes(',')))
    .map((r) => {
      let prueba = r.prueba || '';
      // Aplicar normalización G2 PRIMERO (millas, -Combis...)
      prueba = normalizePruebaName(prueba);
      const lower = prueba.toLowerCase();
      if (lower.startsWith('cross')) prueba = 'Cross';
      else if (lower.startsWith('trail')) prueba = 'Trail';
      else prueba = getBasePrueba(prueba);
      const atletaNorm = stripDiacritics((r.atleta || '').toLowerCase());
      return { ...r, prueba, atletaNorm };
    });
}

function genderFromCategoria(categoria: string): Gender {
  const cat = (categoria || '').toLowerCase();
  if (
    cat.includes('fem') ||
    cat.includes('chica') ||
    cat.includes('junior f') ||
    cat.includes('absoluta f')
  ) {
    return 'F';
  }
  if (
    cat.includes('masc') ||
    cat.includes('chico') ||
    cat.includes('junior m') ||
    cat.includes('absoluta m')
  ) {
    return 'M';
  }
  return '?';
}

export function detectGender(rawData: RawMark[], athleteName: string): Gender {
  for (const r of rawData) {
    if (r.atleta !== athleteName) continue;
    if (r.genero) return r.genero;
    const g = genderFromCategoria(r.categoria);
    if (g !== '?') return g;
  }
  return '?';
}

export function buildGenderIndex(rawData: RawMark[]): Map<string, Gender> {
  const map = new Map<string, Gender>();
  for (const r of rawData) {
    if (map.has(r.atleta) && map.get(r.atleta) !== '?') continue;
    const g: Gender = r.genero ?? genderFromCategoria(r.categoria);
    if (g !== '?' || !map.has(r.atleta)) map.set(r.atleta, g);
  }
  return map;
}
